/**
 * Formatting utility functions
 */

/**
 * Format a number with commas as thousands separators
 * Note: Use formatNumber from parsers.ts for simple integer formatting
 *
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted string with commas
 *
 * @example
 * formatNumberWithDecimals(1234567.89) // "1,234,568"
 * formatNumberWithDecimals(1234567.89, 2) // "1,234,567.89"
 */
export function formatNumberWithDecimals(value: number, decimals: number = 0): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format ISK currency with commas
 *
 * @param value - ISK amount
 * @param includeDecimals - Whether to include decimals (default: false)
 * @returns Formatted ISK string
 *
 * @example
 * formatISK(1234567.89) // "1,234,568 ISK"
 * formatISK(1234567.89, true) // "1,234,567.89 ISK"
 */
export function formatISK(value: number, includeDecimals: boolean = false): string {
  const decimals = includeDecimals ? 2 : 0;
  return `${formatNumberWithDecimals(value, decimals)} ISK`;
}

/**
 * Format a compact number (K, M, B)
 *
 * @param value - Number to format
 * @returns Compact formatted string
 *
 * @example
 * formatCompact(1234) // "1.2K"
 * formatCompact(1234567) // "1.2M"
 * formatCompact(1234567890) // "1.2B"
 */
export function formatCompact(value: number): string {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toString();
}

/**
 * Format ISK currency in compact format
 *
 * @param value - ISK amount
 * @returns Compact formatted ISK string
 *
 * @example
 * formatCompactISK(1234567) // "1.2M ISK"
 */
export function formatCompactISK(value: number): string {
  return `${formatCompact(value)} ISK`;
}
